import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Button, Card, Space, Spin, message, Modal} from 'antd';
import {
    ReloadOutlined,
    CloseOutlined,
    LoadingOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    LeftOutlined,
    RightOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import {PdfViewerProps} from './PdfViewer.types';
import {saveAs} from 'file-saver';
import axios from 'axios';
import {membershipPdfFilename} from '../../utils/reportFilename';

/**
 * Shared PDF Viewer component for displaying PDF documents
 * Supports dynamic loading of PDF.js library
 */
const PdfViewer: React.FC<PdfViewerProps> = ({
                                                 visible,
                                                 onClose,
                                                 title,
                                                 loadPdfData,
                                                 exportPdf,
                                                 exportExcel,
                                                 mode = 'modal',
                                                 reuseLoadedPdf = false
                                             }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pdfDownloading, setPdfDownloading] = useState<boolean>(false);
    const [excelDownloading, setExcelDownloading] = useState<boolean>(false);
    const [libraryLoaded, setLibraryLoaded] = useState<boolean>(false);
    const pdfUrlRef = useRef<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Fallback to iframe if we can't use PDF.js
    // const [useIframe, setUseIframe] = useState<boolean>(false);
    // const iframeRef = useRef<HTMLIFrameElement>(null);

    // PDF.js related states (only used when PDF.js is available)
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [pdfDocument, setPdfDocument] = useState<any>(null);
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageCount, setPageCount] = useState<number>(0);
    const [scale, setScale] = useState<number>(1.0);
    const [rendering, setRendering] = useState<boolean>(false);
    const loadedPdfJsLibraryRef = useRef(false);
    const loadedPdfRef = useRef(false);

    // Load the PDF.js library dynamically
    useEffect(() => {
        if ( (mode === 'modal' && !visible) ) return;

        const loadPdfJsLibrary = async () => {
            if (loadedPdfJsLibraryRef.current) return; // 防止重複載入
            loadedPdfJsLibraryRef.current = true;

            try {
                // Check if PDF.js is already loaded
                if ((window as any).pdfjsLib) {
                    console.log('PDF.js library already loaded');
                    setLibraryLoaded(true);
                    return;
                }

                // Load PDF.js scripts
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
                script.async = true;

                script.onload = () => {
                    // Configure worker
                    if ((window as any).pdfjsLib) {
                        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc =
                            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';

                        console.log('PDF.js library loaded successfully');
                        setLibraryLoaded(true);
                    }
                };

                script.onerror = () => {
                    console.error('Failed to load PDF.js library');
                    // setUseIframe(true); // Fallback to iframe approach
                    setLibraryLoaded(false);
                };

                document.body.appendChild(script);
            } catch (err) {
                console.error('Error loading PDF.js library:', err);
                // setUseIframe(true); // Fallback to iframe approach
                setLibraryLoaded(false);
            }
        };

        loadPdfJsLibrary();
    }, [visible, mode]);

    /**
     * Load PDF data
     */
    const handleLoadPdf = useCallback(async () => {
        if (loadedPdfRef.current) return;
        loadedPdfRef.current = true;
        setLoading(true);
        setError(null);

        // Reset PDF states
        setPdfData(null);
        setPdfBlob(null);
        setPdfDocument(null);
        setPageNum(1);
        setPageCount(0);

        try {

            // Get PDF data from the provided function
            const response = await loadPdfData();
            console.log('Retrieved PDF data:', response);

            let uint8Array: Uint8Array;
            let blob: Blob;

            // Handle different response types
            if (response instanceof Blob) {
                // If already a Blob
                blob = response;
                // Read ArrayBuffer from Blob then create Uint8Array
                const arrayBuffer = await response.arrayBuffer();
                uint8Array = new Uint8Array(arrayBuffer);
            } else {
                // Convert response to Uint8Array (regardless of its type)
                try {
                    // 1. If it's like an ArrayBuffer
                    if (response && typeof response === 'object' && 'byteLength' in response) {
                        uint8Array = new Uint8Array(response as ArrayBuffer);
                    }
                    // 2. If it's like a Uint8Array
                    else if (response && typeof response === 'object' && 'BYTES_PER_ELEMENT' in response) {
                        uint8Array = response as Uint8Array;
                    }
                    // 3. If it's an array
                    else if (Array.isArray(response)) {
                        uint8Array = new Uint8Array(response);
                    }
                    // 4. If it's something else, try direct conversion
                    else {
                        uint8Array = new Uint8Array(response as any);
                    }

                    // Create Blob from Uint8Array
                    blob = new Blob([uint8Array], {type: 'application/pdf'});
                } catch (err) {
                    console.error('Failed to process PDF data:', err);
                    throw new Error('Unsupported PDF data format');
                }
            }

            console.log('Processed PDF blob:', blob);

            // Set states
            setPdfData(uint8Array);
            setPdfBlob(blob);

        } catch (err) {
            loadedPdfRef.current = false;
            if (axios.isCancel(err) || (err instanceof DOMException && err.name === 'AbortError')) return;
            console.error('Failed to load PDF report:', err);
            setError('Unable to load PDF report. Please try again later.');
            message.error('Failed to load PDF report');
        } finally {
            setLoading(false);
        }
    }, [loadPdfData]);

    /**
     * Process PDF data when it's successfully loaded
     */
    useEffect(() => {
        if ( (mode === 'modal' && !visible) ) return;

        const handlePdfData = async () => {
            // First check if we have a PDF blob but no PDF document, which can happen with large PDFs
            if (pdfBlob && !pdfDocument) {
                // If PDF.js is available, use it to process the PDF
                if (libraryLoaded && (window as any).pdfjsLib) {
                    try {
                        console.log('Processing PDF with PDF.js using blob');
                        // Load using blob URL
                        const blobUrl = URL.createObjectURL(pdfBlob);
                        const loadingTask = (window as any).pdfjsLib.getDocument(blobUrl);

                        // Use type assertion to set progress listener
                        // PDF.js has progress events, but TypeScript definitions might be incomplete
                        (loadingTask as any).onProgress = (progressData: { loaded: number, total: number }) => {
                            console.log(`Loading PDF: ${Math.round((progressData.loaded / progressData.total) * 100)}%`);
                        };

                        const doc = await loadingTask.promise;
                        console.log('PDF document loaded successfully');
                        setPdfDocument(doc);
                        setPageCount(doc.numPages);

                        // Ensure page number is within valid range
                        if (pageNum > doc.numPages) {
                            setPageNum(1);
                        }

                        // Clean up blob URL
                        URL.revokeObjectURL(blobUrl);
                    } catch (err) {
                        console.error('PDF.js document loading failed:', err);
                        console.error('Error details:', err);

                        // If PDF.js processing fails, fall back to iframe mode
                        setError(null); // Don't show error, just switch to iframe
                        // setUseIframe(true);
                        console.log('Switched to iframe mode after PDF.js failure');
                    }
                }
            } else if (pdfData && !pdfDocument && libraryLoaded && (window as any).pdfjsLib) {
                // If we have pdfData and PDF.js is available
                try {
                    console.log('Processing PDF with PDF.js using binary data');
                    // Load PDF data using PDF.js
                    const loadingTask = (window as any).pdfjsLib.getDocument({data: pdfData});
                    const doc = await loadingTask.promise;
                    setPdfDocument(doc);
                    setPageCount(doc.numPages);

                    // Ensure page number is within valid range
                    if (pageNum > doc.numPages) {
                        setPageNum(1);
                    }
                } catch (err) {
                    console.error('PDF.js document loading failed:', err);
                    console.error('Error details:', err);

                    // If PDF.js processing fails, fall back to iframe mode
                    setError(null); // Don't show error, just switch to iframe
                    // setUseIframe(true);
                    console.log('Switched to iframe mode after PDF.js failure');
                }
            }
        };

        handlePdfData();
    }, [pdfData, pdfBlob, visible, libraryLoaded, pageNum, pdfDocument, mode]);

    /**
     * Render PDF page when page number or PDF document changes
     */
    useEffect(() => {
        if (!pdfDocument || !canvasRef.current || !libraryLoaded) return;

        const renderPage = async () => {
            setRendering(true);
            try {
                console.log(`Rendering page ${pageNum} of ${pageCount} , scale: ${scale}`);
                // Get and render the page
                const page = await pdfDocument.getPage(pageNum);

                // Set page scaling
                const viewport = page.getViewport({scale});
                const canvas = canvasRef.current;

                // Ensure Canvas element exists
                if (!canvas) {
                    console.error('Canvas element does not exist');
                    return;
                }

                const context = canvas.getContext('2d');

                // Ensure Context exists
                if (!context) {
                    console.error('Cannot get Canvas 2D context');
                    return;
                }

                // 清空 canvas（防止殘影）
                context.clearRect(0, 0, canvas.width, canvas.height);

                // Set Canvas dimensions
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                // Render PDF page to Canvas
                const renderContext = {
                    canvasContext: context,
                    viewport: viewport
                };

                await page.render(renderContext).promise;
                console.log(`Page ${pageNum} rendered successfully`);
            } catch (err) {
                console.error('PDF page rendering failed:', err);
                message.error('PDF page rendering failed');
            } finally {
                setRendering(false);
            }
        };

        renderPage();
    }, [pdfDocument, pageNum, scale, libraryLoaded, pageCount]);

    // Load PDF when visibility or data changes
    useEffect(() => {
        if ( (mode === 'modal' && visible) || mode === 'general') {
            handleLoadPdf();
        }

    }, [visible, mode, handleLoadPdf]);

    // Dispose a document only when it is replaced or the viewer closes.
    useEffect(() => {
        return () => {
            if (pdfDocument) {
                try {
                    pdfDocument.destroy();
                } catch (err) {
                    console.error('Failed to clean up PDF document:', err);
                }
            }
        };
    }, [pdfDocument]);

    useEffect(() => {
        return () => {
            clearPdfUrlRef();
            clearIframeRef();
        };
    }, []);

    /**
     * Go to previous page
     */
    const prevPage = () => {
        if (pageNum <= 1) return;
        setPageNum(pageNum - 1);
    };

    /**
     * Go to next page
     */
    const nextPage = () => {
        if (pageNum >= pageCount) return;
        setPageNum(pageNum + 1);
    };

    /**
     * Zoom in
     */
    const zoomIn = () => {
        setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
    };

    /**
     * Zoom out
     */
    const zoomOut = () => {
        setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
    };

    /**
     * Export PDF
     */
    const handleExportPdf = async () => {
        if (!exportPdf) {
            message.warning('PDF export functionality is not available');
            return;
        }

        setPdfDownloading(true);
        try {
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `export_${dateStr}.pdf`;
            if (reuseLoadedPdf) {
                if (!pdfBlob) return;
                saveAs(pdfBlob, pdfBlob instanceof File ? pdfBlob.name : membershipPdfFilename());
            } else {
                await exportPdf(filename);
            }
            message.success('PDF report exported successfully');
        } catch (err) {
            console.error('Failed to export PDF report:', err);
            message.error('Failed to export PDF report');
        } finally {
            setPdfDownloading(false);
        }
    };

    /**
     * Export Excel
     */
    const handleExportExcel = async () => {
        if (!exportExcel) {
            message.warning('Excel export functionality is not available');
            return;
        }

        setExcelDownloading(true);
        try {
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `export_${dateStr}.xlsx`;
            await exportExcel(filename);
            message.success('Excel report downloaded successfully');
        } catch (err) {
            console.error('Failed to download Excel report:', err);
            message.error('Failed to download Excel report');
        } finally {
            setExcelDownloading(false);
        }
    };

    /**
     * Print the current PDF
     */
    const handlePrint = () => {
        clearPdfUrlRef();
        clearIframeRef();

        if (!pdfBlob) {
            message.warning('沒有可用的 PDF 文件可列印');
            return;
        }

        try {
            const pdfUrl = URL.createObjectURL(pdfBlob);
            pdfUrlRef.current = pdfUrl;

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = pdfUrl;
            iframeRef.current = iframe;

            document.body.appendChild(iframe);


            iframe.onload = () => {
                const iframeWindow = iframe.contentWindow;
                if (!iframeWindow) {
                    message.error('無法存取 iframe.contentWindow');
                    return;
                }

                iframeWindow.focus();
                iframeWindow.print();

            };
        } catch (err) {
            console.error('列印失敗:', err);
            message.error('列印 PDF 失敗');
        }
    };

    // Render PDF viewer content
    const renderPdfViewer = () => {
        if (loading) {
            return (
                <div className="pdf-content-centered">
                    <Spin size="large" tip="Loading PDF report..."/>
                </div>
            );
        }

        if (error) {
            return (
                <div className="pdf-content-centered" style={{color: 'red'}}>
                    {error}
                </div>
            );
        }

        // If using PDF.js mode
        return (
            <div className="pdf-container">
                {/* PDF control toolbar - only keep page navigation and zoom functionality */}
                <div className="pdf-toolbar">
                    <Space size="middle">
                        <Space>
                            <Button
                                icon={<LeftOutlined/>}
                                onClick={prevPage}
                                disabled={pageNum <= 1 || rendering || !pdfDocument}
                                title="Previous Page"
                            />
                            <span className="page-info">{pdfDocument ? `${pageNum} / ${pageCount}` : '-'}</span>
                            <Button
                                icon={<RightOutlined/>}
                                onClick={nextPage}
                                disabled={pageNum >= pageCount || rendering || !pdfDocument}
                                title="Next Page"
                            />
                        </Space>
                        <Space>
                            <Button
                                icon={<ZoomOutOutlined/>}
                                onClick={zoomOut}
                                disabled={scale <= 0.5 || rendering || !pdfDocument}
                                title="Zoom Out"
                            />
                            <span className="zoom-info">{Math.round(scale * 100)}%</span>
                            <Button
                                icon={<ZoomInOutlined/>}
                                onClick={zoomIn}
                                disabled={scale >= 3.0 || rendering || !pdfDocument}
                                title="Zoom In"
                            />
                        </Space>
                    </Space>
                </div>

                {/* PDF display area */}
                <div className="pdf-viewer">
                    {rendering && (
                        <div className="rendering-overlay">
                            <Spin tip="Rendering page..."/>
                        </div>
                    )}
                    {!pdfDocument && pdfData && !error && (
                        <div className="rendering-overlay">
                            <Spin tip="Processing PDF document..."/>
                        </div>
                    )}
                    <div className="pdf-canvas-wrapper">
                        <canvas ref={canvasRef} className="pdf-canvas"/>
                    </div>
                </div>
            </div>
        );
    };

    const clearIframeRef = () => {
        if (iframeRef.current) {
            document.body.removeChild(iframeRef.current);
            iframeRef.current = null;
        }
    }

    const clearPdfUrlRef = () => {
        if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
        }
    }

    const handleOnClose = () => {
        console.log('handleOnClose');
        clearPdfUrlRef();
        clearIframeRef();

        if (onClose) {
            onClose();
        }
    }

    return (
        <>
            {
                mode === 'modal' && (
                    <Modal
                        title={title}
                        open={visible}
                        onCancel={handleOnClose}
                        footer={null}
                        width="80%"
                        style={{top: 20}}
                        styles={{body: {padding: '0px'}}}
                    >
                        <Card
                            bordered={false}
                            extra={
                                <Space>
                                    <Button
                                        icon={<ReloadOutlined/>}
                                        onClick={() => { loadedPdfRef.current = false; void handleLoadPdf(); }}
                                        loading={loading}
                                    >
                                        重新載入
                                    </Button>
                                    <Button
                                        icon={<PrinterOutlined/>}
                                        onClick={handlePrint}
                                        disabled={!pdfBlob}
                                    >
                                        列印
                                    </Button>
                                    {exportPdf && (
                                        <Button
                                            type="primary"
                                            icon={pdfDownloading ? <LoadingOutlined/> : <FilePdfOutlined/>}
                                            onClick={handleExportPdf}
                                            loading={pdfDownloading}
                                            disabled={pdfDownloading || (reuseLoadedPdf && !pdfBlob)}
                                        >
                                            匯出PDF
                                        </Button>
                                    )}
                                    {exportExcel && (
                                        <Button
                                            type="primary"
                                            icon={excelDownloading ? <LoadingOutlined/> : <FileExcelOutlined/>}
                                            onClick={handleExportExcel}
                                            loading={excelDownloading}
                                            disabled={excelDownloading}
                                            style={{background: '#52c41a', borderColor: '#52c41a'}}
                                        >
                                            匯出Excel
                                        </Button>
                                    )}
                                    <Button
                                        icon={<CloseOutlined/>}
                                        onClick={handleOnClose}
                                    >
                                        關閉
                                    </Button>
                                </Space>
                            }
                        >
                            {renderPdfViewer()}
                        </Card>
                    </Modal>
                )
            }
            {
                mode !== 'modal' && (
                    <Card
                        bordered={false}
                        extra={
                            <Space>
                                <Button
                                    icon={<ReloadOutlined/>}
                                    onClick={() => { loadedPdfRef.current = false; void handleLoadPdf(); }}
                                    loading={loading}
                                >
                                    重新載入
                                </Button>
                                <Button
                                    icon={<PrinterOutlined/>}
                                    onClick={handlePrint}
                                    disabled={!pdfBlob}
                                >
                                    列印
                                </Button>
                                {exportPdf && (
                                    <Button
                                        type="primary"
                                        icon={pdfDownloading ? <LoadingOutlined/> : <FilePdfOutlined/>}
                                        onClick={handleExportPdf}
                                        loading={pdfDownloading}
                                        disabled={pdfDownloading || (reuseLoadedPdf && !pdfBlob)}
                                    >
                                        匯出PDF
                                    </Button>
                                )}
                                {exportExcel && (
                                    <Button
                                        type="primary"
                                        icon={excelDownloading ? <LoadingOutlined/> : <FileExcelOutlined/>}
                                        onClick={handleExportExcel}
                                        loading={excelDownloading}
                                        disabled={excelDownloading}
                                        style={{background: '#52c41a', borderColor: '#52c41a'}}
                                    >
                                        匯出Excel
                                    </Button>
                                )}
                                {/*<Button*/}
                                {/*    icon={<CloseOutlined/>}*/}
                                {/*    onClick={handleOnClose}*/}
                                {/*>*/}
                                {/*    關閉*/}
                                {/*</Button>*/}
                            </Space>
                        }
                    >
                        {renderPdfViewer()}
                    </Card>
                )
            }
        </>
    );
};

export default PdfViewer;
